import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useTenant } from '../../../../contexts/TenantContext';

const Footer = () => {
  const { getConfigValue } = useTenant();
  const year = new Date().getFullYear();
  const links: Record<string, string> = {
    Overview: '/overview',
  };
  const solutionsUrl = getConfigValue<string>('SOLUTIONS_URL', '');
  const supportUrl = getConfigValue<string>('SUPPORT_URL', '');
  const privacyUrl = getConfigValue<string>('PRIVACY_POLICY_URL', '');
  const termsUrl = getConfigValue<string>('TERMS_OF_SERVICE_URL', '');
  if (solutionsUrl) links['Bright Solutions'] = solutionsUrl;
  if (supportUrl) links.Help = supportUrl;
  if (privacyUrl) links.Privacy = privacyUrl;
  if (termsUrl) links.Terms = termsUrl;

  return (
    <Box
      sx={{
        alignItems: 'center',
        borderTop: '1px solid',
        borderColor: 'divider',
        justifyContent: 'space-between',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 3, md: 2 },
        py: 2,
        px: 4,
        width: 1,
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <img src="/bright/logo.svg" alt="Bright MLS" style={{ height: 40 }} />
      </Box>
      <Grid
        container
        sx={{
          columnGap: { md: 4 },
          justifyContent: {
            xs: 'center',
            md: 'flex-start',
          },
          rowGap: {
            xs: 2,
            md: 0,
          },
          width: {
            xs: 1,
            md: 'auto',
          },
        }}
      >
        {Object.entries(links).map(([label, href]) => (
          <Grid
            key={label}
            size={{
              xs: 6,
              md: 'auto',
            }}
          >
            <Typography
              variant="body2"
              component="a"
              href={href}
              target={href.startsWith('http') ? '_blank' : undefined}
              rel={href.startsWith('http') ? 'noreferrer' : undefined}
              sx={{
                '&:hover': {
                  textDecoration: 'underline',
                },
                color: 'text.secondary',
                display: 'block',
                textAlign: 'center',
                textDecoration: 'none',
                fontWeight: 600,
              }}
            >
              {label}
            </Typography>
          </Grid>
        ))}
      </Grid>
      <Box sx={{ flexShrink: 0 }}>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            textAlign: { xs: 'center', md: 'right' },
            display: 'block',
          }}
        >
          © {year} Bright MLS. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

const PreviewFooter: React.FC = () => {
  const { isAddEditHost } = useTenant();
  const year = new Date().getFullYear();

  if (!isAddEditHost()) return <Footer />;

  return (
    <Box component="footer" sx={{ mt: 'auto' }}>
      {/* Disclaimer — always shown */}
      <Box
        sx={{
          bgcolor: 'secondary.dark',
          color: 'common.white',
          py: 3,
          px: { xs: 2, md: 4 },
          textAlign: 'center',
          '@media print': {
            bgcolor: 'background.paper',
            color: 'text.primary',
            borderTop: '1px solid',
            borderColor: 'text.primary',
          },
        }}
      >
        <Typography variant="body2" sx={{ fontSize: 12, lineHeight: 1.5 }}>
          &copy;{year} Bright MLS, Inc. All Rights Reserved.
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: 12,
            lineHeight: 1.5,
            mt: 1,
            maxWidth: 800,
            mx: 'auto',
          }}
        >
          The information included in the Listing Preview is a draft of the
          listing prior to publishing to the MLS. It is deemed reliable, but it
          is presented in order to be independently verified.
        </Typography>
      </Box>
    </Box>
  );
};

export default React.memo(PreviewFooter);
